
import React, { useRef, useEffect } from 'react';
import { Box, Flex, Text, IconButton, useColorModeValue } from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { motion } from 'framer-motion';

const MotionBox = motion(Box);

const ModernStepper = ({
  steps,
  activeStep,
  setActiveStep,
  stepPage,
  setStepPage,
  stepsPerPage = 5,
  hasNextPage = false,
  hasPrevPage = false,
  onNextPage,
  onPrevPage,
  startIndex = 0,
}) => {
  const activeRef = useRef(null);
  const scrollContainerRef = useRef(null);

  const containerBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const inactiveColor = useColorModeValue('gray.500', 'gray.400');
  const activeStepBg = useColorModeValue('blue.50', 'whiteAlpha.200');
  const hoverBg = useColorModeValue('blue.50', 'whiteAlpha.200');

  const totalStepPages = Math.ceil(steps.length / stepsPerPage);
  const visibleSteps = steps.slice(
    stepPage * stepsPerPage,
    stepPage * stepsPerPage + stepsPerPage
  );

  const handlePrev = () => {
    if (stepPage > 0) {
      setStepPage((p) => p - 1);
    } else if (hasPrevPage && onPrevPage) {
      onPrevPage();
    }
  };

  const handleNext = () => {
    if (stepPage < totalStepPages - 1) {
      setStepPage((p) => p + 1);
    } else if (hasNextPage && onNextPage) {
      onNextPage();
    }
  };

  const isPrevDisabled = stepPage === 0 && !hasPrevPage;
  const isNextDisabled = stepPage >= totalStepPages - 1 && !hasNextPage;

  return (
    <Box
      bg={containerBg}
      borderRadius="2xl"
      p={2}
      border="1px solid"
      borderColor={borderColor}
      boxShadow="sm"
      position="relative"
      overflow="hidden"
    >
      <Flex align="center" gap={2}>
        <IconButton
          aria-label="Previous visits"
          icon={<ChevronLeftIcon boxSize={6} />}
          size="sm"
          variant="ghost"
          borderRadius="full"
          isDisabled={isPrevDisabled}
          onClick={handlePrev}
          _hover={{
            bg: hoverBg,
            color: 'blue.500',
          }}
        />

        <Flex
          ref={scrollContainerRef}
          flex="1"
          overflowX="auto"
          gap={2}
          sx={{
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
          }}
          p={1}
        >
          {visibleSteps.map((step, localIndex) => {
            const index = stepPage * stepsPerPage + localIndex;
            const globalIndex = startIndex + index;
            const isActive = index === activeStep;

            return (
              <Box
                key={`${index}-${startIndex}`}
                position="relative"
                cursor="pointer"
                onClick={() => setActiveStep(index)}
                flex="1"
                minW="80px"
              >
                <Flex
                  direction="column"
                  align="center"
                  justify="center"
                  py={2}
                  px={3}
                  borderRadius="xl"
                  position="relative"
                  zIndex={1}
                  transition="all 0.3s"
                >
                  <Text
                    fontWeight={isActive ? '800' : '600'}
                    fontSize="lg"
                    color={isActive ? 'blue.600' : inactiveColor}
                    lineHeight="1"
                  >
                    {steps.length - index}
                  </Text>
                  <Text
                    fontSize="10px"
                    fontWeight="500"
                    color={isActive ? 'blue.500' : inactiveColor}
                    mt={1}
                    whiteSpace="nowrap"
                  >
                    {step.label}
                  </Text>
                </Flex>

                {isActive && (
                  <MotionBox
                    layoutId="activeTab"
                    position="absolute"
                    top="0"
                    left="0"
                    right="0"
                    bottom="0"
                    bg={activeStepBg}
                    borderRadius="xl"
                    initial={false}
                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                  />
                )}
              </Box>
            );
          })}
        </Flex>

        <IconButton
          aria-label="Next visits"
          icon={<ChevronRightIcon boxSize={6} />}
          size="sm"
          variant="ghost"
          borderRadius="full"
          isDisabled={isNextDisabled}
          onClick={handleNext}
          _hover={{
            bg: hoverBg,
            color: 'blue.500',
          }}
        />
      </Flex>
    </Box>
  );
};

export default ModernStepper;
